import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Lock,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { authExtended as auth } from '../dashboard/api';
import visionLiveLogo from '../assets/images/visionlive.png';
import mojenaocaleLogo from '../assets/branding/mojenaocale-logo.webp';

// Header component matching landing page style
const Header = ({ onBack }) => {
  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 z-50 bg-[#0d0d0d]/90 backdrop-blur-md border-b border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white/60" />
            </button>
          )}
          <a href="/" className="flex items-center gap-2">
            <img
              src={visionLiveLogo}
              alt="VisionLive"
              className="h-9 w-auto max-w-[220px] object-contain"
            />
          </a>
        </div>
        
        <div className="hidden md:flex items-center gap-8">
          <a href="/#features" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Features
          </a>
          <a href="/#partnership" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Partner Launch
          </a>
          <a href="/#faq" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            FAQ
          </a>
        </div>
      </div>
    </motion.nav>
  );
};

const ResetPasswordPage = ({ onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState(false);
  
  // Form fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    // Extract token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (!tokenParam) {
      setTokenError(true);
    } else {
      setToken(tokenParam);
    }
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await auth.resetPassword(token, password);
      if (result.error) {
        setError(result.error.message || 'Failed to reset password');
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const InputField = ({ icon: Icon, type = 'text', placeholder, value, onChange, ...props }) => (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
      <input
        type={type === 'password' && showPassword ? 'text' : type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-[#111118] border border-white/10 text-white placeholder:text-white/40 focus:border-blue-500 focus:outline-none transition-colors"
        {...props}
      />
      {type === 'password' && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
  );

  // Show error if no token
  if (tokenError) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <Header onBack={onBack} />
        
        <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-80px)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="bg-[#111118] rounded-2xl border border-white/10 p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Invalid Reset Link</h1>
              <p className="text-white/60 mb-6">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
              <a
                href="/dashboard"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors"
              >
                Go to Login
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show success message
  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <Header onBack={onBack} />
        
        <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-80px)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="bg-[#111118] rounded-2xl border border-white/10 p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Password Reset!</h1>
              <p className="text-white/60 mb-6">
                Your password has been successfully reset. You can now sign in with your new password.
              </p>
              <a
                href="/dashboard"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors"
              >
                Sign In
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header onBack={onBack} />
      
      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-80px)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Card */}
          <div className="bg-[#111118] rounded-2xl border border-white/10 p-8">
            <div className="mb-6 flex items-center justify-between rounded-2xl border border-[#3B82F6]/15 bg-[linear-gradient(160deg,rgba(59,130,246,0.12),rgba(17,17,24,0.96))] px-4 py-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#93C5FD]">Live in production</p>
                <p className="mt-1 text-sm text-white/70">VisionLive powers mojenaocale.ba with 300+ frames.</p>
              </div>
              <a
                href="https://mojenaocale.ba"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 items-center rounded-xl bg-white px-3 shadow-lg shadow-black/20"
              >
                <img
                  src={mojenaocaleLogo}
                  alt="MojeNaočale.ba"
                  className="h-5 w-auto object-contain"
                />
              </a>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">Reset your password</h1>
              <p className="text-white/60">Enter your new password below</p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-4 mb-6 rounded-xl bg-red-500/10 border border-red-500/20"
              >
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </motion.div>
            )}

            {/* Reset Password Form */}
            <form onSubmit={handleResetPassword} className="space-y-4">
              <InputField
                icon={Lock}
                type="password"
                placeholder="New password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <InputField
                icon={Lock}
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Reset Password
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
          
          {/* Footer */}
          <p className="text-center text-white/30 text-xs mt-6">
            Remember your password?{' '}
            <a href="/dashboard" className="text-blue-400 hover:text-blue-300 transition-colors">
              Sign in
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
